<template>
    <dashboard
            :statusFilterOptions="statusFilterOptions"
            :riskTypeOptions="riskTypeOptions"
            :tableHeaders="tableHeaders"
            :endpoint="endpoint"
            :actions-endpoint="actionsEndpoint"
            :submission-route="submissionRoute"
            :manual-submission-route="manualSubmissionRoute"
            :statFilters="statFilters"
            :info="info"
            role="insurer"
    >
        <template v-slot:tableRow="props">

            <td>
                {{ props.row.item.updated_at | formatDateEnglishShort}}
            </td>

            <td :inner-html.prop="props.row.item.id | highlightText(props.searchTerm)"/>

            <td style="max-width: 100px">
                <span class='text-scroller ellipsis'
                      @mouseover="scrollTextForwards"
                      @mouseout="scrollTextBackwards"
                      :inner-html.prop="props.row.item.underwriter_reference | highlightText(props.searchTerm)" />
            </td>

            <td style="max-width: 100px">
                <span class='text-scroller client-scroller ellipsis' @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards"
                    :inner-html.prop="props.row.item.client_company_name | highlightText(props.searchTerm)"
                />
            </td>

            <td>
                <span v-if="!isMultiObligor(props.row.item)" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis width-150'
                    :inner-html.prop="props.row.item.obligor_name_notification | highlightText(props.searchTerm)"
                />

                <x-tooltip v-else top>
                    <template v-slot:activator="{ on }">
                            <span v-on="on" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis width-150'
                                :inner-html.prop="props.row.item.obligor_name_notification | highlightText(props.searchTerm)"
                            />
                    </template>
                    <div v-if="props.row.item.obligors.length > 0">
                        <p>Obligors:</p>
                        <p v-for="obligor in props.row.item.obligors">&nbsp;-&nbsp;{{ obligor.name }}</p>
                    </div>
                    <div v-if="props.row.item.guarantors.length > 0">
                        <p>Guarantors:</p>
                        <p v-for="guarantor in props.row.item.guarantors">&nbsp;-&nbsp;{{ guarantor.name }}</p>
                    </div>
                </x-tooltip>
            </td>

            <td style="max-width: 100px">
                <span v-if="!isMultiObligor(props.row.item)" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis'>
                    {{ countryName(props.row.item) }}
                </span>
                <x-tooltip v-else top>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis'>
                            {{ countryName(props.row.item) }}
                        </span>
                    </template>
                    <div v-if="props.row.item.obligors.length > 0">
                        <p>Obligors country:</p>
                        <p v-for="obligorCountry in getUniqueCountryList(props.row.item.obligors)">&nbsp;-&nbsp;{{ obligorCountry }}</p>
                    </div>
                    <div v-if="props.row.item.guarantors.length > 0">
                        <p>Guarantors country:</p>
                        <p v-for="guarantorCountry in getUniqueCountryList(props.row.item.guarantors)">&nbsp;-&nbsp;{{ guarantorCountry }}</p>
                    </div>
                </x-tooltip>
            </td>

            <td class="text-xs-right">
                <span class='text-scroller ellipsis' @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards">
                    {{ props.row.item.limit_of_liability | currency(props.row.item.currency_code) }}
                </span>
            </td>

            <td>
                {{ props.row.item.expiry_date | formatDateEnglishShort }}
            </td>

            <td :inner-html.prop="props.row.item.umr | highlightText(props.searchTerm)"/>

            <td>
                <action-icon
                    v-if="props.actions.pending_amendments"
                    color="blue"
                    icon="edit"
                >
                    Amendment received
                </action-icon>

                <action-icon
                    v-if="props.actions.pending_off_risk"
                    color="grey"
                    icon="fa-power-off"
                >
                    Off risk received
                </action-icon>
                <action-icon
                    v-if="props.actions.pending_mid_term_adjustment"
                    color="grey"
                    icon="fab fa-product-hunt"
                >
                    Mid-term premium adjustment received
                </action-icon>

                <status-chip small :status="props.row.item.status" class="ma-0" />
            </td>

        </template>
    </dashboard>
</template>

<script>
    import Dashboard from "~/components/Shared/Partials/Dashboard";
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';
    import ActionIcon from '~/components/Shared/UIComponents/ActionIcon';
    import Scroll from '~/mixins/scrollText';
    import generalHelpers from "../../mixins/generalHelpers";

    export default {
        name: 'InsurerDashboard',
        components: {
            Dashboard,
            StatusChip,
            ActionIcon
        },
        mixins: [Scroll, generalHelpers],
        props: ['info'],
        data() {
            return {
                hasStats: false,
                endpoint: 'insurer/risk',
                actionsEndpoint: 'insurer/actions-to-review',
                submissionRoute: 'insurer-risk',
                manualSubmissionRoute: 'manual-submission-risk',
                riskTypeOptions: [
                    {
                        value: 'accepted_declaration',
                        text: 'Accepted Declarations'
                    },
                    {
                        value: 'standalone_policy',
                        text: 'Standalone Policies'
                    },
                ],
                statusFilterOptions: [
                    {
                        value: 'requested',
                        text: 'Awaiting Response'
                    },
                    {
                        value: 'offered',
                        text: 'Offered'
                    },
                    {
                        value: 'firm_order',
                        text: 'Firm Order (Review)'
                    },
                    {
                        value: 'accepted',
                        text: 'Accepted'
                    },
                    {
                        value: 'expired',
                        text: 'Expired'
                    },
                    {
                        value: 'declined',
                        text: 'Declined'
                    },
                    {
                        value: 'bound',
                        text: 'Bound'
                    },
                    {
                        value: 'closed',
                        text: 'Closed'
                    },
                    {
                        value: 'off_risk',
                        text: 'Off Risk',
                    },
                    {
                        value: 'ntu',
                        text: 'NTU',
                    }
                ],
                tableHeaders: [
                    {
                        text: 'Updated',
                        value: 'updated_at',
                        sortable: true
                    },
                    {
                        text: 'ID',
                        value: 'id',
                        sortable: true
                    },
                    {
                        text: 'Reference',
                        value: 'underwriter_reference',
                        sortable: true
                    },
                    {
                        text: 'Insured',
                        value: 'client_company_name',
                        sortable: true
                    },
                    {
                        text: 'Obligor/Guarantor',
                        value: 'obligors',
                        sortable: true
                    },
                    {
                        text: 'Country',
                        value: 'country_name',
                        sortable: true
                    },
                    {
                        text: 'Limit of liability',
                        value: 'limit_of_liability',
                        sortable: true,
                        align: 'right'
                    },
                    {
                        text: 'Declaration end',
                        value: 'expiry_date',
                        sortable: true
                    },
                    {
                        text: 'UMR',
                        value: 'umr',
                        sortable: true
                    },
                    {
                        text: 'Status',
                        value: 'status',
                        sortable: true
                    }
                ],
                statFilters: [
                    {
                        key: 'amendment-requests',
                        name: 'Amendment requests',
                        searchKey: 'bound'
                    },
                    {
                        key: 'firm-orders-for-approval',
                        name: 'Firm orders for approval',
                        searchKey: 'firm_order'
                    },
                    {
                        key: 'requests-for-offers',
                        name: 'Requests for offers',
                        searchKey: 'requested'
                    }
                ]
            };
        },
        methods: {
            isMultiObligor(risk) {
                return risk.obligors.length > 1 || risk.guarantors.length > 1;
            },
            getUniqueCountryList (obligors) {
                let obligorCountryList = [];
                obligors.forEach(function(obligor) {
                    obligorCountryList.push(obligor.country_name);
                });

                return [...new Set(obligorCountryList)];
            },
            countryName(risk) {
                if (this.isMultiObligor(risk)) {
                    const uniqueObligors = this.getUniqueCountryList(risk.obligors)

                    return uniqueObligors.length > 1 ? 'Multi country' : uniqueObligors.shift();
                }

                return risk.obligors[0]?.country_name;
            }
        }
    }
</script>

<style scoped lang="scss">
    .width-20 {
        width: rem(20px);
    }

    .x-table.theme--dark tbody tr.item-row td span.width-150 {
        width: rem(150px);
}
</style>
